import { useEffect, useState } from "react";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import { RazorButton, RazorInputField } from "../../../components/razor-reuse";
import { ALL_ICONS } from "../../../assets/icons/AllIcons";
import ReuseModal from "../../../components/fragment/modal/ReuseModal";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/type";
import {
  deleteExportTemplate,
  exportReport,
  getAllReportTemplate,
} from "../../../redux/export";
import { formatDateToYYYYMMDD, formatTime } from "../../../utils/Helper";
import { systemRoles } from "../../../utils/roles";

interface ExportTemplate {
  id: string;
  name: string;
  type: string;
  fields: string[];
  created_by: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  updated_by: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  deleted_by: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export default function ExportIndex() {
  const today = new Date();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth); // for user permission
  const { loading_templates, templates, fetching } = useSelector(
    (state: RootState) => state.exports
  );
  const [activId, setActivId] = useState("");
  const [viewDetails, setViewDetails] = useState<ExportTemplate | null>(null);
  const [activeTab, setActiveTab] = useState("user");
  const [modalOpen, setModalOpen] = useState({
    create: false,
    view: false,
    download: false,
    delete: false,
  });

  const [downloadDetails, setDownloadDetails] = useState({
    start_date: "",
    end_date: "",
    format: "csv",
  });

  const handleDownload = async () => {
    const payload = { ...downloadDetails, template_id: activId };
    const res = await dispatch(exportReport(payload) as any);
    if (res?.payload?.status === 200) {
      triggerModal("download");
      setActivId("");
      setDownloadDetails({ start_date: "", end_date: "", format: "csv" });
    }
  };

  const triggerModal = (type: string) => {
    setModalOpen((prev) => ({
      ...prev,
      [type]: !prev[type as keyof typeof modalOpen],
    }));
  };

  useEffect(() => {
    dispatch(getAllReportTemplate(activeTab) as any);
  }, [activeTab]);

  // Tab link list
  const tabLinkList = [
    {
      label: "User Templates",
      active: activeTab === "user",
      onClick: () => {
        setActiveTab("user");
      },
    },
    {
      label: "Transaction Templates",
      active: activeTab === "transaction",
      onClick: () => {
        setActiveTab("transaction");
      },
    },
  ];

  return (
    <>
      <DashboardLayoutMain loading={loading_templates} title="Exports">
        {templates?.length < 1 ? (
          <section className="bg-white mx-[2rem] grid h-[90%] mt-[20px] place-content-center">
            <div className="space-y-[8px] text-center">
              <figure className="size-fit mx-auto">
                {ALL_ICONS.empty_state}
              </figure>
              <p className="text-[#758089] text-[12px] mt-[4px]">
                No Templates Created Yet
              </p>
              {user?.permissions?.includes(systemRoles[35]) && (
                <RazorButton
                  onClick={() => triggerModal("create")}
                  color="black-light"
                  className="btn"
                >
                  Create A Template
                </RazorButton>
              )}
            </div>
          </section>
        ) : (
          <section className="">
            <section className="bg-white mx-[2rem] pt-[10px] space-y-4 mt-[15px]">
              <div className="flex items-center justify-between mx-[30px] ">
                <h6 className="text-[18px] text-[#252C32] font-semibold">
                  All Exports
                </h6>
                {user?.permissions?.includes(systemRoles[35]) && (
                  <RazorButton
                    onClick={() => triggerModal("create")}
                    color="black-light"
                    className="btn"
                  >
                    Create A Template
                  </RazorButton>
                )}
              </div>
              <TabLink
                list={tabLinkList}
                className="single-customer-tab-link"
              />
            </section>
            <section className="grid md:grid-cols-2 xl:grid-cols-3 gap-[20px] mx-[2rem] mt-[40px]">
              {templates?.map((chi: any, idx: number) => (
                <div
                  key={idx}
                  className="bg-white min-h-[104px] rounded-[8px] px-[18px] py-[10px]"
                >
                  <div className="flex justify-between uppercase">
                    <div className="space-y-[6px]">
                      <p className="text-[#1E1E1E] font-semibold text-[14px] truncate">
                        {chi?.name || "----"}
                      </p>
                      <p className="text-[12px] text-[#758089]">
                        CREATED BY{" "}
                        {`${chi?.created_by?.first_name || "----"} ${
                          chi?.created_by?.last_name || "----"
                        }`}
                      </p>
                      <p className="text-[12px] text-[#758089] mt-[">
                        {formatTime(chi?.updated_at || chi?.created_at)}
                      </p>
                    </div>
                    {user?.permissions?.includes(systemRoles[36]) && (
                      <figure
                        onClick={() => {
                          triggerModal("delete");
                          setActivId(chi?.id);
                        }}
                      >
                        {ALL_ICONS.trashIcon}
                      </figure>
                    )}
                  </div>
                  <div className="flex items-center gap-[6px] [&_button]:px-[10px] [&_button]:py-[6px] [&_button]:rounded-[4px] [&_button]:text-[12px] mt-[20px] [&_button]:inline-flex [&_button]:items-center [&_figure]:mr-[6px] [&_button]:font-medium">
                    {user?.permissions?.includes(systemRoles[37]) && (
                      <button
                        onClick={() => {
                          triggerModal("download");
                          setActivId(chi?.id);
                        }}
                        className="bg-pry !text-white"
                      >
                        <figure className="[&_svg]:size-[12px] [&_path]:!stroke-white">
                          {ALL_ICONS.download_icon}
                        </figure>
                        Download
                      </button>
                    )}
                    {user?.permissions?.includes(systemRoles[35]) && (
                      <button
                        onClick={() => {
                          localStorage.setItem("exportType", chi?.type);
                          localStorage.setItem("exportId", chi?.id);
                          navigate(ALL_ROUTES.DASHBOARD_EXPORT_EDIT);
                        }}
                        className="text-pry border border-pry"
                      >
                        <figure className="[&_svg]:size-[12px] [&_path]:!fill-pry">
                          {ALL_ICONS.edit}
                        </figure>
                        Edit
                      </button>
                    )}
                    {user?.permissions?.includes(systemRoles[34]) && (
                      <button
                        onClick={() => {
                          setViewDetails(chi);
                          triggerModal("view");
                        }}
                        className="text-pry !rounded-[0px] border-b border-pry"
                      >
                        <figure className=" ">{ALL_ICONS.eye}</figure>
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </section>
          </section>
        )}
      </DashboardLayoutMain>

      <ReuseModal
        title="Export Template"
        visible={modalOpen.create}
        onClose={() => triggerModal("create")}
        hideBtn
        disable={false}
      >
        <div className="text-[16px] font-semibold text-black space-y-[10px] mt-[-10px]">
          <figure
            onClick={() => {
              navigate(ALL_ROUTES.DASHBOARD_EXPORT_CREATE);
              localStorage.setItem("exportType", "user");
            }}
            className="border bg-white px-[10px] py-[6px] rounded-[8px] cursor-pointer hover:scale-105 ease-linear transition-all duration-200"
          >
            User Export
          </figure>
          <figure
            onClick={() => {
              navigate(ALL_ROUTES.DASHBOARD_EXPORT_CREATE);
              localStorage.setItem("exportType", "transaction");
            }}
            className="border bg-white px-[10px] py-[6px] rounded-[8px] cursor-pointer hover:scale-105 ease-linear transition-all duration-200"
          >
            Transaction Export
          </figure>
        </div>
      </ReuseModal>

      <ReuseModal
        title="View Details"
        visible={modalOpen.view}
        onClose={() => triggerModal("view")}
        hideBtn
        disable={false}
      >
        <div className="text-[16px]  text-black space-y-[10px] mt-[-10px]">
          <div className="flex justify-between items-center">
            <div className="">
              <p className="text-[14px] text-[#98A2B3]">Created by</p>
              <p className="text-[14px] text-[#101928] font-medium">{`${
                viewDetails?.created_by?.first_name || "----"
              } ${viewDetails?.created_by?.last_name || "----"}`}</p>
            </div>
            <div className="">
              <p className="text-[14px] text-[#98A2B3]">Date Updated</p>
              <p className="text-[14px] text-[#101928] font-medium">
                {formatTime(
                  viewDetails?.updated_at || viewDetails?.created_at || ""
                )}
              </p>
            </div>
          </div>
          <div className="space-y-[6px]">
            <p className="text-[14px] text-[#98A2B3]">Updated Items</p>
            <div className="flex gap-[16px] flex-wrap">
              {viewDetails?.fields?.map((item, ids) => (
                <p
                  key={ids}
                  className="text-[14px] text-[#101928] font-medium uppercase text-nowrap border border-pry p-[4px] px-[8px] rounded-[4px]"
                >
                  {item.replace(/_/g, " ")}
                </p>
              ))}
            </div>
          </div>
        </div>
      </ReuseModal>

      <ReuseModal
        title="Delete Template"
        visible={modalOpen.delete}
        onClose={() => {
          triggerModal("delete");
          setActivId("");
        }}
        btnLabel="Delete"
        disable={false}
        loading={fetching}
        onBtnClick={async () => {
          await dispatch(deleteExportTemplate(activId) as any);
          dispatch(getAllReportTemplate(activeTab) as any);
        }}
        classname="modal-add-user"
      >
        <div className="text-[16px] font-medium text-black mt-[-10px]">
          Are you sure you want to delete this template?
        </div>
      </ReuseModal>

      <ReuseModal
        title="Download Details"
        visible={modalOpen.download}
        onClose={() => {
          triggerModal("download");
          setActivId("");
          setDownloadDetails({ start_date: "", end_date: "", format: "csv" });
        }}
        disable={
          !downloadDetails.start_date || !downloadDetails.end_date || fetching
        }
        btnLabel="Apply & Download"
        loading={fetching}
        onBtnClick={handleDownload}
        classname="modal-add-user"
      >
        <div className="text-[16px] text-black space-y-[10px] mt-[-20px]">
          <p className="text-[14px] text-[#758089]">
            Select the start date and end date to export document
          </p>
          <RazorInputField
            color="black-light"
            type="date"
            label="Start Date"
            placeholder=" Select date"
            onChange={(e: any) => {
              setDownloadDetails((prev) => ({
                ...prev,
                start_date: formatDateToYYYYMMDD(e[0]),
              }));
            }}
            value={downloadDetails.start_date}
            dateOptions={{ maxDate: today }}
          />
          <RazorInputField
            color="black-light"
            type="date"
            label="End Date"
            placeholder=" Select date"
            onChange={(e: any) => {
              setDownloadDetails((prev) => ({
                ...prev,
                end_date: formatDateToYYYYMMDD(e[0]),
              }));
            }}
            value={downloadDetails.end_date}
            dateOptions={{
              minDate: downloadDetails.start_date,
              maxDate: today,
            }}
            disabled={!downloadDetails.start_date}
          />
        </div>
      </ReuseModal>
    </>
  );
}
